import jsonata from 'jsonata';
import { createBlobFromResponse } from '../utils/files/createBlobFromResponse.js';
import { SourceType } from '../constants/restRequestConstants.js';

const HEADER_PATH = /^headers\["([^"]+)"\]$/;
const COOKIE_PATH = /^cookies\["([^"]+)"\](?:\.(.+))?$/;

export class ResponseMapper {
  constructor(
    {
      powerform,
      filesService,
      userService,
      $stateParams,
      responseData,
      headers,
      cookies,
      contentType,
      isBinary,
      mappings,
      ownerSid,
      integrifyForm
    }) {
    this.powerform = powerform;
    this.filesService = filesService;
    this.userService = userService;
    this.$stateParams = $stateParams;
    this.responseData = responseData;
    this.headers = headers || {};
    this.cookies = cookies || [];
    this.contentType = contentType;
    this.isBinary = isBinary;
    this.mappings = mappings;
    this.ownerSid = ownerSid;
    this.integrifyForm = integrifyForm;

    this.processedMappings = new Map();
    this.questionsMap = this._buildQuestionMap();
  }

  async applyMappings() {

    if (!this.powerform || !this.responseData || !this.mappings?.length) {
      console.log("ResponseMapper: No data to map or no mappings configured");
      return;
    }

    const fieldMappings = this.mappings.filter(m =>
      m.mapTo === SourceType.FIELD
      || !m.mapTo); // For backward compatibility - some mappings might not have the `mapTo` property set (default to FIELD)

    if (!fieldMappings.length || fieldMappings.length === 0) {
      console.log("ResponseMapper: No field mappings found");
      return;
    }

    const forEachItem = this.powerform.forEachItem || this._defaultForEachItem;

    forEachItem(this.powerform, async (item) => {
      if (item.type !== 'Question_Type') {
        return;
      }

      const itemMappings = fieldMappings.filter(m => m.fieldId === item.ClientID);

      if (!itemMappings.length) {
        return;
      }

      for (const mapping of itemMappings) {
        try {
          let value = this.processedMappings.get(mapping.responsePath);

          if (value === undefined) {
            value = this._shouldSkipPathEvaluation(item, mapping)
              ? this.responseData
              : await this._extractValue(mapping.responsePath);

            this.processedMappings.set(mapping.responsePath, value ?? null);
          }

          await this._updateFieldValue(item, value, mapping);
        } catch (err) {
          console.error("Error in response mapping:", err);
        }
      }
    });
  }

  _shouldSkipPathEvaluation(item) {
    return this.isBinary && this._isAttachmentField(item);
  }

  async _extractValue(path) {
    try {
      if (path.startsWith('headers[')) {
        return this._extractFromHeaders(path);
      } else if (path.startsWith('cookies[')) {
        return this._extractFromCookies(path);
      } else {
        // Default to body for all other paths
        return await this._extractValueFromPath(path);
      }
    } catch (error) {
      console.error(`Error extracting value from path ${path} in source ${source}:`, error);
      return undefined;
    }
  }

  // #region TODO: Move to shared library after moving to monorepo
  // to share same logic with api-integrations

  // TODO: This is also in integrify-libs
  // Use the integrify-libs version after the code refactor to monorepo
  async _extractValueFromPath(path) {
    try {

      if (!path || !this.responseData) {
        return undefined;
      }

      if (path === "$") {
        return this.responseData;
      }

      if (path.includes("[*]")) {
        const basePath = path.split("[*]")[0].replace(/^\$\.?/, "");
        const remainingPath = path.split("[*]")[1]?.replace(/^\./, "") || "";

        // Get the array at base path
        const array = basePath
          ? await jsonata(basePath).evaluate(this.responseData)
          : this.responseData;

        if (!Array.isArray(array)) {
          return undefined;
        }

        if (!remainingPath) {
          // Return full array if no remaining path
          return array;
        } else {
          // Process each array element with remaining path
          const results = await Promise.all(
            array.map(async (item) => {
              try {
                return remainingPath
                  ? await jsonata(remainingPath).evaluate(item)
                  : item;
              } catch (e) {
                console.warn("Error processing array item:", e);
                return undefined;
              }
            })
          );

          return results.filter((x) => x !== undefined);
        }
      }

      const cleanPath = path.replace(/^\$\./, ''); // remove leading "$."
      const expression = jsonata(cleanPath);
      const value = await expression.evaluate(this.responseData);
      return value;
    } catch (error) {
      console.error("Error extracting value from path:", error);
      return undefined;
    }
  }

  /**
     * Find header key with case-insensitive matching
     */
  _findHeaderKey(headers, headerName) {
    if (headers[headerName]) {
      return headerName;
    }

    // Case-insensitive fallback
    return Object.keys(headers).find(key =>
      key.toLowerCase() === headerName.toLowerCase()
    );
  }

  _parseHeaderPath(path) {
    const match = HEADER_PATH.exec(path);
    return match ? match[1] : null;
  }

  _parseCookiePath(path) {
    const match = COOKIE_PATH.exec(path);
    if (match) {
      return {
        cookieName: match[1],
        property: match[2] || 'value'
      };
    }
    return null;
  }

  _extractFromHeaders(path) {
    // Header path format: headers["headerName"]
    const headerName = this._parseHeaderPath(path);
    if (headerName) {
      const headerKey = this._findHeaderKey(this.headers, headerName);
      return headerKey ? this.headers[headerKey] : undefined;
    }

    return undefined;
  }

  _extractFromCookies(path) {
    // Cookie path format: cookies["cookieName"].property
    const cookieInfo = this._parseCookiePath(path);
    if (cookieInfo) {
      const cookie = this.cookies.find(c => c.name === cookieInfo.cookieName);
      return cookie ? cookie[cookieInfo.property] : undefined;
    }

    return undefined;
  }

  // #endregion TODO: Move to shared library after moving to monorepo

  async _updateFieldValue(field, value, mapping) {
    if (this._isAttachmentField(field)) {
      const filename = await this._resolveMappedValue(mapping.filename);
      const contentType = await this._resolveMappedValue(mapping.contentType);
      return this._updateAttachmentFieldValue(field, value, filename, contentType);
    }

    switch (field.QuestionType) {
      case "DbSelectList":
        if (field.multiple) {
          field.multiChoiceAnswer = Array.isArray(value)
            ? value.map(String)
            : String(value)
              .split(",")
              .map((v) => v.trim());

          field.Answer = field.multiChoiceAnswer.join(", ");
        } else {
          field.Answer = String(value);
        }

        break;

      case "ContactSearch":
      case "MultiContactSearch":
        // Placeholder for contact search logic (requires external service).
        break;

      default:
        field.Answer = value;
    }

    if (field.events?.onChange) {
      field.events.onChange(field.Answer);
    }
  }

  async _resolveMappedValue(map) {
    if (!map || map.source === 'fixed') {
      return map?.value || '';
    } else if (map.source === 'field') {
      const field = this.questionsMap[map.fieldId];
      return field?.Answer || '';
    }
    return '';
  }

  async _updateAttachmentFieldValue(field, data, filename, contentType) {
    const blob = createBlobFromResponse(data, contentType);

    const file = new File([blob], filename || `${field.ClientID}_${Date.now()}`, {
      type: blob.type || contentType || 'application/octet-stream'
    });

    const uploadResult = await this.filesService.upload({
      parentFolder: field.uploadParentFolder || 'temp',
      path: field.uploadPath || '/',
      files: [file]
    });

    if (!Array.isArray(field.Answer)) {
      field.Answer = [];
    }

    if (!field.multi) {
      field.Answer = []; // Clear previous if single file mode
    }

    for (const fileInfo of uploadResult) {

      const structuredFile = await this._createFileObject(fileInfo)
      field.Answer.push(structuredFile);

      if (this.integrifyForm) {
        this.integrifyForm[field.id].$setDirty();
        this.integrifyForm[field.id].modified = true;
      }
    }
  }

  async _createFileObject(fileInfo) {

    const fileKey = fileInfo.sid;

    const structuredFile = {
      name: fileInfo.originalFilename,
      text: fileInfo.originalFilename,
      Size: fileInfo.size,
      Type: fileInfo.contentType,
      file_key: fileKey,
      file_id: fileKey,
      object_sid: fileKey,
      owner_sid: this.ownerSid || null,
      version: fileInfo.version?.number,
    };

    if (fileInfo.filePath?.startsWith('temp')) {
      structuredFile.context_type = 'temp';
      structuredFile.context_sid = await this._getUserSID();
    } else {
      structuredFile.context_type = 'formruntime';
      structuredFile.context_sid = this.$stateParams.recipientTaskSid;
    }

    structuredFile.url = this.filesService.getFileUrl(structuredFile);

    return structuredFile;
  }

  async _getUserSID() {
    const currentUser = await this.userService.get();
    return currentUser.SID;
  }

  _isAttachmentField(field) {
    return ['FileAttachment', 'MultiFileAttachment', 'AIBox'].includes(field.QuestionType);
  }

  _buildQuestionMap() {
    const map = {};
    (this.powerform?.questions || []).forEach(q => {
      map[q.ClientID] = q;
    });
    return map;
  }

  _defaultForEachItem(powerform, callback) {
    powerform.layout?.forEach(section => {
      section.contents?.forEach(container => {
        container.columns?.forEach(column => {
          column.items?.forEach(callback);
        });
      });
    });
  }
}
